/**
 * Uplifted from `apps/web/src/helper/versionHelper.ts` — WEB ONLY.
 *
 * Mobile has no `helper/versionHelper` at all (verified by glob), and the versions this parses
 * belong to the NW-app WebView shell, which mobile does not have. So this folder holds a single
 * `index.desktop.ts` and no `shared.ts`.
 *
 * Uplifted because `helpers/localServer.getLSRequestPayloadData` calls `getVersionNumber`
 * internally — there is no caller to pass the number in from.
 *
 * 🔴 **ONE DELIBERATE DEPENDENCY PRUNE, and it is behaviour-preserving.** The app's
 * `getVersionNumber` obtains `nwAppVersion` via `commonHelpers.getCurrentVersions()`, which also
 * assembles three git-info fields from `helper/generatedGitInfo.json` — a file GENERATED INTO THE
 * APP by `npm run git-info`. Core must never import from `apps/*`, so dragging
 * `getCurrentVersions` across is not an option.
 *
 * It does not need to be: `getCurrentVersions().nwAppVersion` is exactly
 * `getLocalStorage(LOCALSTORAGE_KEYS.nwAppVersion) || ""`, and `getVersionNumber` reads NOTHING
 * else off that object. The three git-info fields are dead weight on this path. So this reads
 * the same localStorage key directly. Externally identical — the probe asserts old-vs-new
 * equivalence across the version-string cases, including the malformed ones that return `NaN`.
 */
import { getLocalStorage } from "../../utils/localStorage";
import { LOCALSTORAGE_KEYS } from "../../utils/constants";

/**
 * Returns the last number of the version stored in local storage.
 * If the version format is not valid or cannot be parsed, NaN is returned.
 *
 * @return {number} The last number of the version stored in local storage, or NaN if the version format is invalid.
 */
export const getVersionNumber = (): number => {
  let nwAppVersion = getLocalStorage(LOCALSTORAGE_KEYS.nwAppVersion) || "";
  if (!nwAppVersion || typeof nwAppVersion !== "string") return NaN;
  nwAppVersion = nwAppVersion.replace("_beta", "");
  const versionSplit = nwAppVersion.split(".");
  if (
    versionSplit.length !== 4 ||
    versionSplit[0] !== "1" ||
    versionSplit[1] !== "0" ||
    versionSplit[2] !== "0"
  )
    return NaN;
  const lastNumber = Number(versionSplit[3]);
  if (isNaN(lastNumber)) return NaN;
  return lastNumber;
};
